/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * Native (Capacitor) OIDC deeplink listener.
 *
 * On the Android shell the OIDC `redirect_uri` is the app-owned deeplink
 * `br.com.k4d.housien://authentication/callback` (see `native-oidc.ts`). When
 * Authien finishes authenticating, Android routes that deeplink back into the
 * app and `@capacitor/app` fires an `appUrlOpen` event carrying the full URL
 * (including the OIDC `?code=...&state=...`).
 *
 * This module subscribes to that event, hands the URL to
 * `UserManager.signinCallback()` (which performs the real PKCE code→token
 * exchange plus `state`/`nonce` validation), and then routes the WebView to the
 * in-app `/authentication/callback` page so the existing post-auth UX
 * (welcome + redirect to the workspace) runs exactly as on the web.
 *
 * The listener is a NO-OP on the web: `isNativePlatform()` is false there, so it
 * never subscribes. This keeps the web sign-in path byte-for-byte unchanged.
 *
 * Security: delivering the code over an app-owned scheme (instead of the dead
 * `https://localhost` origin) does not weaken any check — `signinCallback`
 * still validates PKCE, `state`, and `nonce` before a session is established.
 */

import { App } from '@capacitor/app';
import type { URLOpenListenerEvent } from '@capacitor/app';
import type { PluginListenerHandle } from '@capacitor/core';
import { getUserAuthManager } from '@context/authentication';
import { getRealLocalStorage } from '@shared/utils/storage/localStorageWrapper';
import { ensureRoutableSigninState } from '@shared/utils/oidc-signin-state-repair';
import { extractOidcCallbackFromDeeplink, isNativePlatform } from './native-oidc';

/** In-app route the WebView shows while/after the callback is processed. */
const IN_APP_CALLBACK_PATH = '/authentication/callback';

/**
 * Completes an OIDC sign-in from a deeplink callback URL.
 *
 * Repairs the pending signin-state's routing tag (same guard the web callback
 * uses), runs the real `signinCallback` PKCE exchange, then navigates the
 * WebView to the in-app callback route so the normal post-auth flow runs. On
 * failure it still routes to the callback page, whose retry/err handling takes
 * over (it never dead-ends on `localhost`).
 *
 * @param callbackUrl The deeplink callback URL from `appUrlOpen`.
 * @returns A promise that resolves once the callback has been processed.
 */
export async function completeNativeOidcCallback(callbackUrl: string): Promise<void> {
  const manager = getUserAuthManager();
  // Repair request_type BEFORE signinCallback reads it (native round-trips can
  // drop it — see oidc-signin-state-repair.ts).
  ensureRoutableSigninState(callbackUrl, getRealLocalStorage());
  try {
    if (manager) {
      await manager.signinCallback(callbackUrl);
    }
  } catch (error) {
    // Surface for diagnostics; the in-app callback page handles recovery UX.
    console.error('[native-oidc] signinCallback failed:', error);
  } finally {
    // Route to the SPA callback route (served from the bundle at
    // https://localhost) so the existing welcome + post-auth redirect runs.
    if (window.location.pathname !== IN_APP_CALLBACK_PATH) {
      window.location.replace(IN_APP_CALLBACK_PATH);
    }
  }
}

/**
 * Registers the native OIDC deeplink listener. No-op on the web.
 *
 * @returns A cleanup function that removes the listener (safe to call on the
 *          web, where nothing was registered).
 */
export function registerNativeOidcDeeplinkListener(): () => void {
  if (!isNativePlatform()) {
    return () => undefined;
  }

  let handlePromise: Promise<PluginListenerHandle> | null = null;

  handlePromise = App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
    const callbackUrl = extractOidcCallbackFromDeeplink(event.url);
    if (callbackUrl) {
      void completeNativeOidcCallback(callbackUrl);
    }
  });

  return () => {
    void handlePromise?.then((handle) => handle.remove());
  };
}
